import { cn } from '@commander/utils';
import * as React from 'react';
import { XIcon as X } from '../../icons';

export interface ToastProps extends React.HTMLAttributes<HTMLDivElement> {
  onClose?: () => void;
  variant?: 'default' | 'success' | 'error' | 'warning';
}

const Toast = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ className, children, onClose, variant = 'default', ...props }, ref) => {
    const variantStyles = {
      default: 'bg-background text-foreground border',
      success: 'bg-success/10 text-success border-success/20 border',
      error: 'bg-destructive/10 text-destructive border-destructive/20 border',
      warning: 'bg-warning/10 text-warning border-warning/20 border',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex items-start gap-3 rounded-lg p-4 pr-10 shadow-lg transition-all duration-300 animate-in slide-in-from-top-2 fade-in',
          variantStyles[variant],
          className
        )}
        {...props}
      >
        {children}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute right-2 top-2 rounded-md p-1 opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  }
);
Toast.displayName = 'Toast';

// Popup component (lighter weight than Toast)
export interface PopupProps extends React.HTMLAttributes<HTMLDivElement> {
  onClose?: () => void;
}

const Popup = React.forwardRef<HTMLDivElement, PopupProps>(
  ({ className, children, onClose, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'relative inline-flex items-center gap-2 rounded-md bg-background border px-4 py-2 text-sm shadow-md transition-all duration-300 animate-in slide-in-from-top-1 fade-in',
          className
        )}
        {...props}
      >
        {children}
        {onClose && (
          <button
            onClick={onClose}
            className="ml-2 opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Close"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }
);
Popup.displayName = 'Popup';

export { Toast, Popup };
